import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { FILES_BASE_URL } from "../../../api/baseURL";
import {
  removeSessionDocument,
  validateClaimDocument,
} from "../../../api/vistaService";
import exampleAccidentPhoto from "../../../assets/images/claim-example-accident-photo.png";
import exampleLicenseBack from "../../../assets/images/claim-example-driving-license-back.png";
import exampleLicenseFront from "../../../assets/images/claim-example-driving-license-front.png";
import exampleGdtReport1 from "../../../assets/images/claim-example-gdt-report-1.png";
import exampleGdtReport2 from "../../../assets/images/claim-example-gdt-report-2.png";
import exampleOwnershipBack from "../../../assets/images/claim-example-vehicle-ownership-back.png";
import exampleOwnershipFront from "../../../assets/images/claim-example-vehicle-ownership-front.png";
import AcceptedFilesNote from "../../../components/common/acceptedFilesNote";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import CustomCheckbox from "../../../components/common/customCheckbox/customCheckbox";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import useFileClaimFlow from "../../../hooks/useFileClaimFlow";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../hooks/useMotorFormData";
import { selectNewMotorForm } from "../../../redux/features/newMotorFormSlice";
import {
  removeClaimFile,
  storeClaimFile,
} from "../../../utils/claimFilesStorage";
import { cn } from "../../../lib/utils";
import { getClientSource } from "../../../utils/getClientSource";
import {
  DocumentResultCard,
  DocumentScanCard,
} from "./documentCards";
import {
  claimSessionFileId,
  findingsAnswerOvertaken,
  mergeCrossChecks,
  noteFindingsApplied,
  takeFindingsTicket,
} from "./useVistaSession";
import ValidationNotices, { findingLines } from "./validationNotices";
import {
  UPLOAD_ACCIDENT_PHOTOS,
  UPLOAD_DRIVER_LICENSE,
  UPLOAD_INCIDENT_REPORT,
  UPLOAD_INSURANCE_CERTIFICATE,
  UPLOAD_VEHICLE_OWNERSHIP,
} from "./data";
import FileClaimMotorLayout from "./fileClaimMotorLayout";
import {
  AccidentDocs,
  DriverLicencesDocs,
  IncidentInput,
  InsuranceCert,
  OwnershipDocs,
} from "./uploadDocsPage";

// One wizard page per required document (Figma "File a Claim Solidarity
// Policy Holder", frames 07-12; the third-party vehicle-damage flow reuses
// the same pages plus an Insurance Certificate page). Each page shows a
// single upload slot, an example of the required document, and validates
// each file with Vista as soon as it is uploaded ("AI OCR - Analyzing
// document…" → "Document verified successfully").
//
// `slotLabels` are the Vista document types the page's files are sent under,
// in slot order — one for a single-label slot, two for a front/back document.
// They are also the key into the case type's document config, which is where
// `fallbackMaxFiles` gets overruled: how many files a slot takes is Vista
// admin's decision, not this file's (see resolveSlotMax).
const STEP_CONFIG = {
  [UPLOAD_DRIVER_LICENSE]: {
    slotName: "driver_license",
    reduxKey: DriverLicencesDocs,
    subtitle_en:
      "Please upload the Driver license at the time of accident (Front & Back)",
    subtitle_ar: "يرجى تحميل رخصة القيادة وقت الحادث (أمامية وخلفية)",
    // Bahraini driving licenses always have two sides — require both.
    minFiles: 2,
    fallbackMaxFiles: 2,
    slotLabels: ["Driving License 1", "Driving License 2"],
    example: {
      title_en: "Example of the driver license",
      title_ar: "مثال على رخصة القيادة",
      images: [exampleLicenseFront, exampleLicenseBack],
    },
  },
  [UPLOAD_VEHICLE_OWNERSHIP]: {
    slotName: "vehicle_ownership_certificate",
    reduxKey: OwnershipDocs,
    subtitle_en:
      "Please upload the Vehicle ownership certificate (Front & Back)",
    subtitle_ar: "يرجى تحميل شهادة ملكية المركبة (أمامية وخلفية)",
    minFiles: 2,
    fallbackMaxFiles: 2,
    slotLabels: ["Vehicle Ownership 1", "Vehicle Ownership 2"],
    example: {
      title_en: "Example of the vehicle ownership certificate",
      title_ar: "مثال على شهادة ملكية المركبة",
      images: [exampleOwnershipFront, exampleOwnershipBack],
    },
  },
  [UPLOAD_INSURANCE_CERTIFICATE]: {
    slotName: "insurance_certificate",
    reduxKey: InsuranceCert,
    subtitle_en: "Please upload your Insurance Certificate",
    subtitle_ar: "يرجى تحميل شهادة التأمين الخاصة بك",
    // Third-party vehicle damage only (Vista handover 2026-08-17 round 2
    // §6): the claimant's own policy — Vista reads the coverage type and
    // blocks comprehensive holders (Claimant Coverage Type check).
    fallbackMaxFiles: 5,
    slotLabels: ["Insurance Certificate"],
    // No Figma frame / example asset for this page yet — the example block
    // is omitted rather than shown with a wrong document.
  },
  [UPLOAD_INCIDENT_REPORT]: {
    slotName: "incident_report",
    reduxKey: IncidentInput,
    subtitle_en: "Please upload the Incident Report",
    subtitle_ar: "يرجى تحميل تقرير الحادث من إدارة المرور",
    // A station-issued traffic report is a multi-page paper form, and
    // customers photograph it a page at a time rather than producing one
    // scan — so Vista's slot takes several files (5 at the time of writing;
    // the config is what decides).
    fallbackMaxFiles: 5,
    slotLabels: ["GDT Accident Report"],
    // "uploads", not "pages" and not "photos". The report itself has pages,
    // but this counts FILES — calling it "photos" would be wrong about a
    // scanned page, and calling it "pages" conflates one file with one page
    // of the report. Arabic has no comfortable count noun for "uploads", so
    // it keeps the natural word for files.
    unit_en: "uploads",
    unit_ar: "ملفات",
    // Vista judges completeness per FILE, and that logic was written when the
    // whole report arrived as ONE document. Page by page, a five-page report
    // genuinely is incomplete: most pages come back complete:false with a note
    // about a table or a page number continuing somewhere else. Rendered per
    // file that is a missing-pages error on nearly every upload, so this slot
    // gets one soft, slot-wide hint instead — see multiPageHint below.
    multiPageReport: true,
    example: {
      title_en: "Example of the incident / GDT report",
      title_ar: "مثال على تقرير الحادث من إدارة المرور",
      images: [exampleGdtReport1, exampleGdtReport2],
    },
  },
  [UPLOAD_ACCIDENT_PHOTOS]: {
    slotName: "photos_of_accident",
    reduxKey: AccidentDocs,
    subtitle_en: "Please upload Photos of the accident",
    subtitle_ar: "يرجى تحميل صور الحادث",
    fallbackMaxFiles: 10,
    slotLabels: ["Accident Photos"],
    unit_en: "photos",
    unit_ar: "صور",
    // The one slot Vista does not read a PDF on — confirmed live, twice: not
    // a consequence of taking several files (GDT Accident Report does too,
    // and keeps PDF), just this slot's own rule.
    imagesOnly: true,
    example: {
      title_en: "Correct photo of a traffic accident",
      title_ar: "الصورة الصحيحة لحادث مروري",
      note_en:
        "The accident is photographed from one of the four angles, showing the location of the accident and the involved vehicles",
      note_ar:
        "يتم تصوير الحادث من إحدى الزوايا الأربع بحيث يظهر موقع الحادث والمركبات المتورطة",
      images: [exampleAccidentPhoto],
      singleImage: true,
    },
  },
};

// Vista's per-request ceiling on the mode=full submit at Review: that is ONE
// multipart POST carrying every document of the claim, and multer aborts the
// 17th file with a flat 400 LIMIT_UNEXPECTED_FILE (Vista answers 2026-07-14
// Q5) — the case is then never created and the adjusters get no Vista report.
//
// So 16 is a budget for the WHOLE claim, not for any one slot. It used to be
// spent up front, by writing each slot a fixed share into the config above
// (the photos slot cut to 8 in the vehicle-damage flow, the insurance
// certificate held at 2 of the 5 Vista allows, the report at 2). That
// arithmetic cannot survive limits that come from Vista's admin — the report
// alone went from 2 to 5 — so the budget is now enforced where it actually
// binds: every page offers its full configured allowance minus what the
// claim's other slots are already holding, and says so when that is what runs
// out first.
const VISTA_SUBMIT_FILE_CAP = 16;

// The Vista document type a file in this slot is sent under. Front/back
// documents have a label per side; every other slot labels all of its files
// the same, which is what lets a slot hold more files than it has labels.
const slotLabelAt = (config, index) =>
  config.slotLabels[Math.min(index, config.slotLabels.length - 1)];

/**
 * How many files this slot takes, according to Vista.
 *
 * A page's ceiling is the sum of its labels' `maxUploads`: a front/back
 * document is two slots of one file each, the report is one slot of several
 * pages. Anything the config does not describe falls back to the built-in
 * number rather than to a guess — a partial config must not silently shrink a
 * slot the customer needs.
 *
 * @param {Object} config - the page's STEP_CONFIG entry
 * @param {Object|null} documentLimits - slots by documentType, or null
 * @returns {number}
 */
const resolveSlotMax = (config, documentLimits) => {
  if (!documentLimits) return config.fallbackMaxFiles;
  let total = 0;
  for (const label of config.slotLabels) {
    const maxUploads = documentLimits[label]?.maxUploads;
    if (!Number.isFinite(maxUploads) || maxUploads < 1) {
      console.warn(
        `[Vista] no maxUploads for "${label}" — using the built-in ceiling for ${config.slotName}`
      );
      return config.fallbackMaxFiles;
    }
    total += maxUploads;
  }
  return total;
};

// A slot's `acceptedFormats` may come back as MIME types or as bare
// extensions; both mean the same thing to the customer.
const MIME_BY_FORMAT = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  heif: "image/heif",
  webp: "image/webp",
  gif: "image/gif",
  bmp: "image/bmp",
  tif: "image/tiff",
  tiff: "image/tiff",
  avif: "image/avif",
  pdf: "application/pdf",
};

const asMimeType = (format) => {
  const value = String(format || "").trim().toLowerCase();
  if (!value) return null;
  if (value.includes("/")) return value;
  return MIME_BY_FORMAT[value.replace(/^\./, "")] || null;
};

/**
 * What this slot takes, for the note in the drop area.
 *
 * Vista's config knows what each slot accepts, per slot, and that is what
 * the customer is told — but only the part of it this page's dropzone will
 * actually take. The two disagree on Accident Photos: Vista's config still
 * lists PDF for it, while live validation rejects one (see `imagesOnly`), so
 * the dropzone never offers PDF there. Advertising the config verbatim put
 * "PDF" in the note above an input that refused it. The note must never name
 * a type the input will not accept, so the config is intersected with the
 * dropzone's list. Falls back to the dropzone's list when the config is
 * unavailable, silent, or has nothing in common with it.
 *
 * @param {Object} config - the page's STEP_CONFIG entry
 * @param {Object|null} documentLimits - slots by documentType, or null
 * @returns {string[]} MIME types
 */
const resolveSlotAcceptedTypes = (config, documentLimits) => {
  const dropzoneTypes = Object.keys(dropzoneAcceptFor(config));
  const configured = config.slotLabels
    .map((label) => documentLimits?.[label]?.acceptedFormats)
    .find((formats) => Array.isArray(formats) && formats.length);
  const types = (configured || [])
    .map(asMimeType)
    .filter((type) => type && dropzoneTypes.includes(type));
  return types.length ? types : dropzoneTypes;
};

// Vista omits involvedDocuments on missing-data warnings (it only sends
// them on true cross-document mismatches — raw-probe 2026-07-14), so pin
// every known check to the page(s) whose documents it inspects.
const CHECK_NAME_SLOTS = {
  "Chassis Number Extracted": ["vehicle_ownership_certificate"],
  "License Number Extracted": ["driver_license"],
  "Date of Birth Extracted": ["driver_license"],
  "Accident Date Validity": ["incident_report"],
  "Owner Name Consistency": [
    "vehicle_ownership_certificate",
    "incident_report",
  ],
  "Address Consistency": ["vehicle_ownership_certificate", "driver_license"],
  "Plate Number Consistency": [
    "vehicle_ownership_certificate",
    "incident_report",
  ],
  accident_photos_vehicle_no_with_gdt_report_validation: [
    "photos_of_accident",
    "incident_report",
  ],
  "Accident Photos Plate Coverage": ["photos_of_accident", "incident_report"],
  "Claimant Coverage Type": ["insurance_certificate"],
};

const ALL_SLOT_NAMES = Object.values(STEP_CONFIG).map(
  (config) => config.slotName
);

// Does a Vista document label ("Driving License 1", "GDT Accident Report",
// …) belong to the given upload slot?
const slotMatchesVistaLabel = (slotName, label = "") => {
  if (slotName === "driver_license") return label.startsWith("Driving License");
  if (slotName === "vehicle_ownership_certificate")
    return label.startsWith("Vehicle Ownership");
  if (slotName === "insurance_certificate")
    return label === "Insurance Certificate";
  if (slotName === "incident_report") return label === "GDT Accident Report";
  if (slotName === "photos_of_accident") return label === "Accident Photos";
  return false;
};

// Cross-document findings from Vista that implicate a given document, shown on
// every implicated page. Vista marks a non-pass result `blocking: true` when it
// must stop Continue (which checks block is a Vista DB flag — never keyed by
// name here); everything else is advisory.
//
// Module scope rather than render scope: removing a file has to ask which slots
// a finding rests on, and neither helper reads any component state.
const isActionableCheck = (check) => {
  if (check.status === "pass") return false;
  // "Could not validate: …" warnings mean a prerequisite document hasn't been
  // uploaded or read yet — nothing the user can act on, skip them.
  if (
    check.status === "warning" &&
    (check.description || "").startsWith("Could not validate")
  )
    return false;
  return true;
};

const involvedSlotsOf = (check) => {
  const involved = check.involvedDocuments || [];
  if (involved.length === 0) {
    const slots = CHECK_NAME_SLOTS[check.checkName];
    if (slots) return slots;
    return check._sourceSlot ? [check._sourceSlot] : [];
  }
  return ALL_SLOT_NAMES.filter((slot) =>
    involved.some((doc) => slotMatchesVistaLabel(slot, doc.documentType))
  );
};

// Vista's `complete === false` (evidence of missing pages, handover
// 2026-08-17 round 2 §4) is a WARNING for now: claims want incomplete
// reports stopped, but Vista saw a genuine 2-page report judged incomplete
// (both pages labelled "page 2"), so blocking waits on the false-positive
// rate against real Bahraini station reports. Flip this to true to hold
// Continue on such a document — no other change needed.
//
// It reaches single-document slots only. A slot the customer fills a page at
// a time (`multiPageReport`) is expected to report incomplete pages while it
// is being filled, and is never held on the verdict.
const INCOMPLETE_DOCUMENT_BLOCKS = false;

// Redux key for the e-traffic acknowledgment (handover 2026-08-17 round 2
// §3 B1): { acknowledged: true } once the customer confirmed, on the report
// page, that the at-fault party acknowledged fault in the e-traffic app.
// Kept outside the file objects (redux deep-freezes those) and ready to be
// forwarded to CRM once claims decide they want it.
export const ETrafficAck = "ETrafficAcknowledged";

// Vista rate-limit backoff shared across the upload pages of one session.
//
// While this window is open NOTHING is sent to Vista — which is the single
// way this flow can stop calling the API with nothing whatever in the network
// tab to say so. The portal's quota is 12/min AND 200/day per policy, and a
// day of testing reaches the second one; a daily-bucket 429 answers with a
// Retry-After measured in hours, which used to disable every document check
// until the page was hard-reloaded.
//
// So the window is capped. A cap does not get the quota back, but it keeps the
// flow honest: it retries, it 429s again, and it says so in the console
// instead of going quiet for the rest of the afternoon.
const MAX_RATE_LIMIT_BACKOFF_MS = 2 * 60 * 1000;
let vistaRateLimitUntil = 0;

// Vista's server-side multer cap is 10MB per file (400 LIMIT_FILE_SIZE
// above it) — keep the client cap at or below that.
// Same decelerating curve the rest of the claim flow animates on.
const EASE_OUT = [0.22, 1, 0.36, 1];

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// Vista's real accepted formats, confirmed live against production — not a
// rule derived from maxUploads. It looked like one (every multi-upload slot
// tried so far had rejected PDF), but GDT Accident Report is also a
// multi-upload slot and DOES take a PDF, so that inference was wrong the
// moment it was checked against a second slot. Accident Photos is the one
// confirmed exception, and only that one is encoded — `config.imagesOnly`
// below, set on nothing else.
//
// Neither list offers HEIC, WEBP or the other formats the old wide `image/*`
// entry used to widen for: Vista reads jpg/jpeg/png (plus pdf where that is
// allowed) and nothing else, so there is nothing left for that trick to
// widen. A HEIC straight off an iPhone is not a format any claim slot reads;
// the customer needs a JPG or a PNG.
//
// The extensions are still listed explicitly rather than relying on the MIME
// type alone: react-dropzone's validator matches a file's NAME against an
// entry that starts with a dot, and falls back to it when `file.type` is
// empty — which Firefox does for formats the OS has no handler for. jpg/png
// rarely hit that, but there is no reason to leave the guard off files that
// might.
const STANDARD_ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/pdf": [".pdf"],
};

// Accident Photos only: no PDF entry, so react-dropzone (and the OS file
// picker's own filter) never offers one for this slot.
const PHOTO_ONLY_ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

// What this page's dropzone actually accepts.
const dropzoneAcceptFor = (config) =>
  config.imagesOnly ? PHOTO_ONLY_ACCEPT : STANDARD_ACCEPT;

const generateUniqueIdentifier = () =>
  Date.now().toString(36) + "-" + Math.random().toString(36).substring(2, 8);

// Cheap deterministic signature of the file bytes (djb2 over the base64),
// used only to spot the same file uploaded twice in one slot — not security.
const contentSignature = (base64) => {
  let hash = 5381;
  for (let i = 0; i < base64.length; i++) {
    hash = ((hash << 5) + hash + base64.charCodeAt(i)) | 0;
  }
  return `${base64.length}:${hash}`;
};

// Normalized signature of Vista's extracted fields for one document. Two
// photos of the SAME side extract the same values (name, numbers, dates),
// while front and back extract different field sets — so equal signatures
// mean the user most likely uploaded one side twice.
const EXTRACTION_META_KEYS = new Set([
  "documentValidation",
  "crossValidation",
  "caseNumber",
  "reportUrl",
  "bucketUrl",
]);
const extractionSignature = (data) => {
  if (!data || typeof data !== "object") return "";
  const entries = Object.entries(data)
    .filter(
      ([key, value]) =>
        !EXTRACTION_META_KEYS.has(key) &&
        value !== null &&
        value !== undefined &&
        typeof value !== "object" &&
        String(value).trim() !== ""
    )
    .map(([key, value]) => [
      key.toLowerCase(),
      String(value).trim().toLowerCase(),
    ])
    .sort((a, b) => (a[0] < b[0] ? -1 : 1));
  return entries.length ? JSON.stringify(entries) : "";
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Same 3-step files-api sequence the legacy upload page uses
// (token → upload → link).
const uploadClaimFileToFilesApi = async (file) => {
  const base64 = await readFileAsBase64(file);
  file._contentSig = contentSignature(base64);

  const tokenResponse = await fetch(FILES_BASE_URL + "/tokens/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      secret: "Claim123",
      agent: getClientSource(),
      item_type: "claim_docs",
      item_id: file.name + "_" + generateUniqueIdentifier(),
      source: getClientSource(),
      description: "Files token request from customer portal in claims.",
    }),
  });
  if (!tokenResponse.ok) throw new Error("files-api token request failed");
  const tokenResult = await tokenResponse.json();

  const uploadResponse = await fetch(FILES_BASE_URL + "/files/upload", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      token: tokenResult.data.token,
      source: getClientSource(),
      file_name: file.name,
      file_base64: base64,
    }),
  });
  if (!uploadResponse.ok) throw new Error("files-api upload failed");
  const uploadResult = await uploadResponse.json();

  const linkResponse = await fetch(FILES_BASE_URL + "/links/request", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      file_id: uploadResult.data.file_id,
      ip: "",
      validity: 7200,
      reference: "customer portal",
    }),
  });
  if (!linkResponse.ok) throw new Error("files-api link request failed");
  const linkResult = await linkResponse.json();

  return linkResult.data.link;
};

function UploadDocStepPage({
  sessionRef,
  policyNumber,
  driverCpr,
  crossChecks,
  setCrossChecks,
  documentLimits,
}) {
  const translate = useLanguageTranslate();
  const reduceMotion = useReducedMotion();
  const {
    currentStep,
    currentSubstep,
    handleNextSubstep,
    handlePrevSubstep,
  } = useFileClaimFlow();
  const config = STEP_CONFIG[currentSubstep];

  const [files, setFiles] = useState([]);
  // e-traffic report acknowledgment: restored from redux when navigating
  // Back to an already-confirmed report page, cleared when the e-traffic
  // report is removed.
  const [eTrafficAcknowledged, setETrafficAcknowledged] = useState(false);
  const { saveMotorDataInRedux: saveETrafficAck } = useMotorForm(
    ETrafficAck,
    (saved) => setETrafficAcknowledged(saved?.acknowledged === true)
  );
  // _key -> "uploading" | "analyzing" | "verified" | "scan_failed" |
  // "wrong_document" (Vista's documentValidation says the file failed the
  // type check) | "unreadable" (Vista could not read the document — wrong
  // type or bad capture; coarser pre-documentValidation signal) |
  // "uploaded" (validation unavailable) | "upload_failed"
  const [fileStates, setFileStates] = useState({});
  // The Vista session handles this page's files already hold. Seeded from
  // redux on restore: without that a new upload would claim the entry that
  // belongs to a file the page is already showing.
  const claimedFileIdsRef = useRef(new Set());
  const { saveMotorDataInRedux } = useMotorForm(config.reduxKey, (docs) => {
    const restored = Array.isArray(docs) ? docs : [];
    setFiles(restored);
    // Restore the final per-file badge stamped on the file objects, so
    // navigating Back keeps the verified/failed states.
    setFileStates((prev) => {
      const next = { ...prev };
      restored.forEach((file) => {
        if (file?._key && file?.vistaStatus) next[file._key] = file.vistaStatus;
        if (file?._vistaFileId) claimedFileIdsRef.current.add(file._vistaFileId);
      });
      return next;
    });
  });

  // What this slot's own config allows, and what the claim has left of its
  // 16-document submit budget. The other slots are read from redux — this
  // page's files only land there on Continue/Back, which is exactly why its
  // own count comes from state instead.
  const motorFormData = useSelector(selectNewMotorForm);
  const slotMax = resolveSlotMax(config, documentLimits);
  const acceptedTypesForSlot = resolveSlotAcceptedTypes(config, documentLimits);
  const otherSlotFileCount = (currentStep?.substeps || [])
    .filter((substep) => substep !== currentSubstep)
    .reduce((total, substep) => {
      const otherConfig = STEP_CONFIG[substep];
      if (!otherConfig) return total;
      const docs = motorFormData?.[otherConfig.reduxKey];
      return total + (Array.isArray(docs) ? docs.length : 0);
    }, 0);
  // Never below what the page already holds: a customer who filled this slot
  // and then went back and forth must not find their own files over a limit
  // they cannot do anything about.
  const budgetRoom = Math.max(0, VISTA_SUBMIT_FILE_CAP - otherSlotFileCount);
  const maxFiles = Math.max(files.length, Math.min(slotMax, budgetRoom));
  // Which of the two ceilings the customer is actually up against — the copy
  // has to name the right one, or "you can only upload 2 pages here" reads as
  // a lie about a slot that takes five.
  const limitedByClaimBudget = maxFiles < slotMax;
  const unitLabel = translate(config.unit_en || "files", config.unit_ar || "ملفات");
  const claimBudgetMessage = translate(
    `A claim can carry up to ${VISTA_SUBMIT_FILE_CAP} documents in total. To add more here, please remove one from another step.`,
    `يمكن أن تحتوي المطالبة على ${VISTA_SUBMIT_FILE_CAP} مستندًا كحد أقصى. لإضافة المزيد هنا، يرجى إزالة مستند من خطوة أخرى.`
  );

  // Pending validation-retry timers, cleared on unmount so a scheduled
  // retry never fires against an unmounted page.
  const retryTimersRef = useRef({});
  const validationAttemptsRef = useRef({});
  // Files removed while Vista was still reading them. Their answer describes a
  // document that has left the claim, so it is dropped rather than stamped on
  // the page — otherwise the finding the customer just removed comes back.
  const removedKeysRef = useRef(new Set());
  const scannedKeysRef = useRef(new Set());
  useEffect(() => {
    const timers = retryTimersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  const setFileState = (file, status, { stamp = true } = {}) => {
    if (stamp) file.vistaStatus = status;
    setFileStates((prev) => ({ ...prev, [file._key]: status }));
  };

  const scheduleValidationRetry = (file, slotIndex, delayMs) => {
    clearTimeout(retryTimersRef.current[file._key]);
    retryTimersRef.current[file._key] = setTimeout(() => {
      delete retryTimersRef.current[file._key];
      runVistaValidation(file, slotIndex);
    }, delayMs);
  };

  const runVistaValidation = (file, slotIndex) => {
    // Cancelled while its upload was still running: nothing has been sent to
    // Vista for it, so there is nothing to read and nothing to take back out.
    if (removedKeysRef.current.has(file._key)) return;
    if (!sessionRef) {
      setFileState(file, "uploaded");
      return;
    }
    const attempts = (validationAttemptsRef.current[file._key] || 0) + 1;
    validationAttemptsRef.current[file._key] = attempts;
    if (attempts > 3) {
      // Repeatedly rate-limited or erroring — give up gracefully
      // (validation is advisory, the claim is never blocked on it).
      setFileState(file, "uploaded");
      return;
    }

    // Inside a rate-limit window: wait it out instead of skipping, so the
    // document still gets its automated check.
    const rateLimitWaitMs = vistaRateLimitUntil - Date.now();
    if (rateLimitWaitMs > 0) {
      console.warn(
        `[Vista] skipping the check for "${file.name}" — rate-limit backoff, ${Math.ceil(
          rateLimitWaitMs / 1000
        )}s left. No request is being sent.`
      );
      setFileState(file, "uploaded");
      scheduleValidationRetry(file, slotIndex, rateLimitWaitMs + 500);
      return;
    }

    setFileState(file, "analyzing", { stamp: false });

    // Claimed before the request goes out, so a removal asked for after this
    // read cannot be applied over the findings the read comes back with.
    const findingsTicket = takeFindingsTicket();
    validateClaimDocument({
      file,
      documentType: slotLabelAt(config, slotIndex),
      sessionRef,
      policyNumber,
      driverCpr,
    }).then((result) => {
      if (removedKeysRef.current.has(file._key)) {
        // Cancelled mid-read. Vista finished anyway — the document is in the
        // session now — so it goes straight back out, named by the handle this
        // very response carries. Without a handle there is no way to name it
        // without taking the slot's other files too, so it is left alone.
        claimSessionFileId(
          file,
          slotLabelAt(config, slotIndex),
          result?.sessionFiles,
          claimedFileIdsRef.current
        );
        if (file._vistaFileId) {
          claimedFileIdsRef.current.delete(file._vistaFileId);
          dropFromVistaSession(
            slotLabelAt(config, slotIndex),
            file._vistaFileId
          );
        }
        return;
      }
      if (!result) {
        // Transient failure (network error / 5xx) — one quick retry, then
        // fall back to plain "uploaded".
        setFileState(file, "uploaded");
        if (attempts < 3) scheduleValidationRetry(file, slotIndex, 3000);
        return;
      }
      if (result.rateLimited) {
        vistaRateLimitUntil =
          Date.now() +
          Math.min(
            (result.retryAfterSeconds || 60) * 1000,
            MAX_RATE_LIMIT_BACKOFF_MS
          );
        setFileState(file, "uploaded");
        scheduleValidationRetry(
          file,
          slotIndex,
          vistaRateLimitUntil - Date.now() + 500
        );
        return;
      }
      if (result.scanFailed) {
        setFileState(file, "scan_failed");
        return;
      }
      // Cross-document findings describe the whole session's document set,
      // so merge them even when this particular file failed its own check.
      const checks = result.crossValidation?.checks || [];
      if (checks.length && setCrossChecks) {
        noteFindingsApplied(findingsTicket);
        setCrossChecks((prev) =>
          mergeCrossChecks(prev, checks, config.slotName)
        );
      }
      // The handle Vista minted for this file, which is the only way to name
      // it again when the customer removes it.
      claimSessionFileId(
        file,
        slotLabelAt(config, slotIndex),
        result.sessionFiles,
        claimedFileIdsRef.current
      );
      // Signature of what Vista extracted from this file — compared across
      // the two slots of a front+back document to spot "same side twice".
      file._vistaExtractionSig = extractionSignature(result.data);
      // Which side of a two-sided card this file shows ("front" | "back" |
      // "both" | null) — Vista side detection, advisory only (2026-07-15).
      const docValidation = Array.isArray(result.data?.documentValidation)
        ? result.data.documentValidation[0]
        : null;
      file._vistaSide = docValidation?.side || null;
      // Issuing country (ISO 3166-1 alpha-2, null when undetermined) and
      // whether Vista recognised it as non-Bahraini (2026-08-17). A foreign
      // licence/ownership is one complete document (no front/back), and a
      // foreign accident report's location must not be resolved to a
      // Bahraini governorate — Review reads _vistaForeign for that.
      file._vistaCountry = docValidation?.country || null;
      file._vistaForeign = docValidation?.foreign === true;
      // Report kind for the GDT slot ("e-traffic" | "physical" | "other";
      // null on non-report slots; undefined before Vista ships it) — drives
      // the e-traffic acknowledgment and whether the report number is a
      // real police report number for CRM (handover 2026-08-17 round 2 §3).
      file._vistaReportKind = docValidation?.reportKind;
      // Completeness verdict: true whole, false = evidence of missing pages
      // (completenessNote says what), null = not judgeable (round 2 §4).
      file._vistaComplete = docValidation?.complete;
      file._vistaCompletenessNote = docValidation?.completenessNote || null;
      if (result.wrongDocument) {
        // documentValidation says the type check failed — it's the wrong
        // document, not a bad capture.
        setFileState(file, "wrong_document");
        return;
      }
      if (!result.docValid) {
        // output.status === false on a single-file call: Vista could not
        // read this document (wrong type / nothing extractable). The user
        // must remove it and upload a proper capture.
        setFileState(file, "unreadable");
        return;
      }
      // Raw extracted fields, kept on the file (through redux) so Review can
      // enrich the CRM createclaim payload with them. Only a verified
      // document's extraction is trustworthy enough to forward.
      file._vistaExtraction = result.data;
      setFileState(file, "verified");
    });
  };

  const processFiles = (incoming) => {
    const offered = Array.from(incoming);
    if (!offered.length) return;
    // Take what fits and say what did not, rather than refusing the whole
    // drop: someone photographing a five-page report a page at a time has no
    // reason to lose the two pages that would have gone in.
    const room = maxFiles - files.length;
    const accepted = offered.slice(0, Math.max(0, room));
    const refused = offered.length - accepted.length;
    if (refused > 0) {
      toast.error(
        limitedByClaimBudget
          ? claimBudgetMessage
          : translate(
              `You can upload up to ${maxFiles} ${unitLabel} here — ${refused} file(s) were not added.`,
              `يمكنك تحميل ${maxFiles} ${unitLabel} كحد أقصى هنا — لم تتم إضافة ${refused} ملف (ملفات).`
            )
      );
    }
    if (!accepted.length) return;

    // Take the lowest slot index nothing is holding, rather than counting the
    // files. On a two-sided document the index picks the Vista label, so
    // counting meant removing "Driving License 1" and uploading a replacement
    // gave it index 1 — a second "Driving License 2" colliding with the file
    // that was already there, and Vista never receiving side 1 at all.
    const takenIndices = new Set();
    files.forEach((file, position) => {
      // A file restored from redux should still carry its _slotIndex; falling
      // back to its position means the worst case is the old behaviour rather
      // than handing a new upload an index somebody is already using.
      takenIndices.add(
        Number.isInteger(file._slotIndex) ? file._slotIndex : position
      );
    });
    const claimFreeIndex = () => {
      let index = 0;
      while (takenIndices.has(index)) index += 1;
      takenIndices.add(index);
      return index;
    };

    const prepared = accepted.map((file) => {
      const fileKey = generateUniqueIdentifier();
      // Keep the original blob for the Vista submit at Review
      storeClaimFile(fileKey, file);
      return Object.assign(file, {
        preview: URL.createObjectURL(file),
        _key: fileKey,
        uploaded: false,
        _slotIndex: claimFreeIndex(),
      });
    });

    setFiles((prev) => [...prev, ...prepared]);
    prepared.forEach((file) => startUpload(file, file._slotIndex));
  };

  const startUpload = (file, slotIndex) => {
    file._slotIndex = slotIndex;
    setFileState(file, "uploading", { stamp: false });
    uploadClaimFileToFilesApi(file)
      .then((link) => {
        file.preview = link;
        file.uploaded = true;
        runVistaValidation(file, slotIndex);
      })
      .catch((error) => {
        console.error("[UploadDocStepPage] upload failed", error);
        setFileState(file, "upload_failed");
      });
  };

  const handleRetryUpload = (file) => {
    startUpload(file, file._slotIndex ?? 0);
  };

  const handleRemove = (fileToRemove) => {
    // Read before the state is torn down: a file Vista is still reading is not
    // in its session yet, so removing it there has to wait for the read.
    const wasReading = ["uploading", "analyzing"].includes(
      stateOf(fileToRemove)
    );
    if (fileToRemove._key) {
      removeClaimFile(fileToRemove._key);
      clearTimeout(retryTimersRef.current[fileToRemove._key]);
      delete retryTimersRef.current[fileToRemove._key];
      delete validationAttemptsRef.current[fileToRemove._key];
      removedKeysRef.current.add(fileToRemove._key);
    }
    const remaining = files.filter((file) => file._key !== fileToRemove._key);
    setFiles(remaining);
    setFileStates((prev) => {
      const next = { ...prev };
      delete next[fileToRemove._key];
      return next;
    });
    // Every cross-document finding that rests on this slot was computed
    // against a document set that no longer exists, so it goes with the file,
    // now, before Vista is even asked. Without this the customer removes the
    // photo Vista complained about and the complaint stays on screen — still
    // holding Continue if it was a blocking check, with nothing left on the
    // page to fix. Vista's answer replaces these a moment later with the
    // checks re-run over what is left.
    //
    // Slot-wide rather than per-file on purpose: the photo slot labels every
    // file "Accident Photos", so there is no way to tell which of them a
    // finding used, and a stale finding is worse than a missing one.
    if (setCrossChecks) {
      setCrossChecks((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (involvedSlotsOf(next[key]).includes(config.slotName)) {
            delete next[key];
          }
        });
        return next;
      });
    }
    if (fileToRemove._vistaReportKind === "e-traffic") {
      setETrafficAcknowledged(false);
      saveETrafficAck(null);
    }
    removeFromVistaSession(fileToRemove, remaining, wasReading);
  };

  // Take the removed document out of Vista's session as well. The session
  // holds every document Vista has read and cross-checks each new one against
  // all of them, so a file only removed in the browser goes on demanding a
  // plate photo for a vehicle that has left the claim — the bug this call
  // exists to fix.
  //
  // A multi-file slot is named one file at a time, by the handle Vista minted
  // for it; the label on its own would take that file's siblings with it. A
  // file with no handle either never reached Vista or was read before handles
  // shipped, so it is only removable when it is the sole file its label
  // covers — dropping the label then drops exactly it.
  const removeFromVistaSession = (fileToRemove, remaining, wasReading) => {
    if (!sessionRef) return;
    // A read still in flight will land after this, and Vista adds the document
    // when it does — so removing it now would race the request that puts it
    // there. The read's own response takes it out instead.
    if (wasReading) return;
    const labelOf = (file) => slotLabelAt(config, file._slotIndex ?? 0);
    const documentType = labelOf(fileToRemove);
    const fileId = fileToRemove._vistaFileId;
    if (fileId) claimedFileIdsRef.current.delete(fileId);
    if (!fileId && remaining.some((file) => labelOf(file) === documentType)) {
      console.warn(
        `[Vista] no session handle for the removed "${documentType}" — leaving the session untouched rather than dropping its siblings`
      );
      return;
    }

    dropFromVistaSession(documentType, fileId);
  };

  const dropFromVistaSession = (documentType, fileId) => {
    const findingsTicket = takeFindingsTicket();
    removeSessionDocument({ sessionRef, documentType, fileId }).then(
      (result) => {
        // A failed call, or an expired session (no crossValidation), leaves
        // the findings this page already cleared for the removed slot.
        const checks = result?.crossValidation?.checks;
        if (!checks || !setCrossChecks) return;
        // This answer describes the session as it stood when the file went.
        // If a read has landed since, the session has moved on and this is a
        // photograph of the past — applying it would erase what that read
        // found, blocking checks included.
        if (findingsAnswerOvertaken(findingsTicket)) {
          console.warn(
            `[Vista] ignoring the removal answer for "${documentType}" — a later read has already answered`
          );
          return;
        }
        noteFindingsApplied(findingsTicket);
        setCrossChecks((prev) => {
          // Vista re-ran the checks over what is left, so its answer is the
          // truth about the slot the file came out of: those findings go,
          // including the ones it has stopped returning altogether, which is
          // what clears a blocking check here and now instead of on the next
          // upload.
          //
          // Only that slot, though. The response carries the checks Vista
          // chose to re-run, not every check that still stands, so replacing
          // the whole board with it used to drop live findings on documents
          // this removal never touched — removing an accident photo could
          // clear a blocking plate mismatch over on the report page.
          const kept = {};
          Object.entries(prev).forEach(([key, check]) => {
            if (!involvedSlotsOf(check).includes(config.slotName)) {
              kept[key] = check;
            }
          });
          return mergeCrossChecks(kept, checks, config.slotName);
        });
      }
    );
  };

  const onDropRejected = (rejections) => {
    rejections.forEach((rejection) => {
      const code = rejection.errors?.[0]?.code;
      if (code === "file-too-large") {
        toast.error(
          translate(
            `"${rejection.file.name}" is too large. Maximum file size is 10MB.`,
            `"${rejection.file.name}" كبير جدًا. الحد الأقصى لحجم الملف هو 10 ميغابايت.`
          )
        );
      } else {
        toast.error(
          config.imagesOnly
            ? translate(
                `"${rejection.file.name}" is not a supported file type. Please upload a JPG or PNG photo.`,
                `"${rejection.file.name}" ليس نوع ملف مدعومًا. يرجى تحميل صورة بصيغة JPG أو PNG.`
              )
            : translate(
                `"${rejection.file.name}" is not a supported file type. Please upload a JPG, PNG or PDF.`,
                `"${rejection.file.name}" ليس نوع ملف مدعومًا. يرجى تحميل ملف بصيغة JPG أو PNG أو PDF.`
              )
        );
      }
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: processFiles,
    onDropRejected,
    accept: dropzoneAcceptFor(config),
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: maxFiles > 1,
  });

  const stateOf = (file) => fileStates[file._key] || "uploaded";
  const isBusy = files.some((file) =>
    ["uploading", "analyzing"].includes(stateOf(file))
  );
  const hasBlockingFailure = files.some((file) =>
    ["scan_failed", "wrong_document", "unreadable", "upload_failed"].includes(
      stateOf(file)
    )
  );
  // Front+back documents (license, ownership) require both sides before
  // Continue — a front-only claim just stalls with the adjusters later.
  // A single VERIFIED file Vista says contains both sides (a 2-page scan)
  // satisfies the requirement; such passes are auditable via Vista's
  // portal_attempts log, so a wrong "both" call can be reviewed later.
  // A verified NON-BAHRAINI document also counts as complete on its own:
  // foreign licences/ownerships aren't split into our front/back scheme.
  // Vista usually returns side "both" for them, but side comes from the
  // extraction LLM and can be "front" on a single-sided doc — `foreign` is
  // the reliable signal (Vista handover 2026-08-17).
  const minFiles = config.minFiles || 1;
  const hasForeignFile = files.some(
    (file) => stateOf(file) === "verified" && file._vistaForeign
  );
  const hasBothSidesFile = files.some(
    (file) => stateOf(file) === "verified" && file._vistaSide === "both"
  );
  const requiredCount = hasBothSidesFile || hasForeignFile ? 1 : minFiles;

  // Keyed by the crossChecks key (checkName + involved documents), which is
  // stable across re-runs — the notice list animates per finding, so an
  // index would make a cleared check look like the one below it changing
  // its text.
  const slotNotices = Object.entries(crossChecks || {})
    .filter(
      ([, check]) =>
        isActionableCheck(check) &&
        involvedSlotsOf(check).includes(config.slotName)
    )
    .map(([key, check]) => ({ ...check, _noticeKey: key }));
  // A blocking check holds Continue on the LAST involved page in wizard
  // order — the page where every involved document exists (e.g. plate
  // consistency fires on the report page, photo plate coverage on the
  // photos page) — so the customer can still go back and fix whichever
  // document is wrong; the message itself shows on all involved pages.
  const slotOrder = (currentStep?.substeps || [])
    .map((substep) => STEP_CONFIG[substep]?.slotName)
    .filter(Boolean);
  const hasBlockingCheck = slotNotices.some((check) => {
    if (check.blocking !== true) return false;
    const lastInvolvedSlot = involvedSlotsOf(check)
      .filter((slot) => slotOrder.includes(slot))
      .sort((a, b) => slotOrder.indexOf(a) - slotOrder.indexOf(b))
      .pop();
    return !lastInvolvedSlot || lastInvolvedSlot === config.slotName;
  });

  // e-traffic intimation (Vista reportKind): the customer must confirm the
  // at-fault party acknowledged fault in the app before continuing.
  const isETrafficReport = files.some(
    (file) =>
      stateOf(file) === "verified" && file._vistaReportKind === "e-traffic"
  );
  const needsETrafficAck = isETrafficReport && !eTrafficAcknowledged;

  // Vista found evidence the document is missing pages. `complete` is null on
  // anything it cannot judge (a photo, a single image), which is silent.
  const incompleteFiles = files.filter(
    (file) => stateOf(file) === "verified" && file._vistaComplete === false
  );

  // Single-document slots — a licence, an ownership card, a certificate —
  // keep the verdict they have always had: Vista is talking about THE
  // document, so the note names the file it read and can hold Continue if
  // claims ever ask for that.
  const hasIncompleteFile = !config.multiPageReport && incompleteFiles.length > 0;

  // The multi-page report is the opposite case, and needs the opposite
  // handling. Vista judges each page on its own, so page 2 of a 4-page report
  // truthfully reports complete:false — per file that would put a
  // missing-pages error on nearly every upload of a perfectly good report.
  // What the customer can actually use is one quiet line about the report as
  // a whole:
  //   - never per file, and never an error — this is advice, and Continue is
  //     never held on it (a report may legitimately be one page);
  //   - only while the slot still has room, since "add the remaining pages"
  //     is only worth saying to someone who can;
  //   - only once every file has settled, so it does not blink in and out
  //     between page uploads;
  //   - only for a "physical" report, the station-issued paper form that HAS
  //     further pages. An "e-traffic" intimation is printed from the GDT app
  //     and is complete at one page; "other" is a Najm / Basher / civil
  //     defence report. Promising more pages on either is just wrong.
  const showMultiPageHint =
    config.multiPageReport &&
    !isBusy &&
    files.length < maxFiles &&
    incompleteFiles.some((file) => file._vistaReportKind === "physical");

  const continueDisabled =
    files.length < requiredCount ||
    isBusy ||
    hasBlockingFailure ||
    hasBlockingCheck ||
    needsETrafficAck ||
    (INCOMPLETE_DOCUMENT_BLOCKS && hasIncompleteFile);
  const needsOtherSide = files.length > 0 && files.length < requiredCount;
  // Which side the single uploaded file shows, for a side-aware hint.
  const uploadedSide =
    needsOtherSide &&
    files.length === 1 &&
    ["front", "back"].includes(files[0]._vistaSide)
      ? files[0]._vistaSide
      : null;

  // Advisory only, never blocks. Vista's side detection is authoritative
  // when it called both files; otherwise fall back to byte-identical files
  // or identical extracted values — either way the user most likely
  // uploaded the same side twice.
  const bothSidesKnown =
    files.length === 2 &&
    ["front", "back"].includes(files[0]._vistaSide) &&
    ["front", "back"].includes(files[1]._vistaSide);
  const duplicateSideSuspected =
    minFiles === 2 &&
    files.length === 2 &&
    !isBusy &&
    !hasBlockingFailure &&
    (bothSidesKnown
      ? files[0]._vistaSide === files[1]._vistaSide
      : (files[0]._contentSig &&
          files[0]._contentSig === files[1]._contentSig) ||
        (files[0]._vistaExtractionSig &&
          files[0]._vistaExtractionSig === files[1]._vistaExtractionSig));
  const duplicatedSide =
    duplicateSideSuspected && bothSidesKnown ? files[0]._vistaSide : null;

  // One list, one vocabulary. Every finding on this page comes from the same
  // read of the same documents, so they are built together and rendered as
  // one severity-ordered stack rather than five boxes in source order.
  const validationNotices = [
    // flatMap, not map: a check carrying several findings — a plate missing
    // from the photos for each of four vehicles — becomes one row each, so
    // nothing arrives as a paragraph of separate problems.
    ...slotNotices.flatMap((check) =>
      findingLines(check, translate).map((text, part) => ({
        key: `check:${check._noticeKey}:${part}`,
        // Which findings block Continue is Vista's DB flag, never keyed by
        // name here; a blocking finding is the only one that reads as an
        // error.
        severity:
          check.status === "fail" || check.blocking === true
            ? "error"
            : "warning",
        text,
      }))
    ),
    ...(hasIncompleteFile
      ? [
          {
            key: "incomplete",
            severity: "warning",
            text: translate(
              "The uploaded document appears to be missing pages — please make sure you uploaded the complete document.",
              "يبدو أن المستند المرفوع ناقص الصفحات، يرجى التأكد من رفع المستند كاملاً."
            ),
            details: incompleteFiles.map(
              (file) =>
                file.name +
                (file._vistaCompletenessNote
                  ? ` — ${file._vistaCompletenessNote}`
                  : "")
            ),
          },
        ]
      : []),
    // The slot takes more than the claim has room for, and the customer has
    // used what was left. That is something to act on, so it goes in the
    // findings stack with everything else rather than sitting in the caption
    // above — which only ever states the number, never argues with it.
    ...(limitedByClaimBudget && files.length >= maxFiles
      ? [
          {
            key: "claim-budget",
            severity: "info",
            text: claimBudgetMessage,
          },
        ]
      : []),
    // One line for the slot, no filenames and no per-page notes: which page
    // Vista thought was cut off is not something the customer can act on, and
    // listing them turns one gentle nudge back into a wall of errors.
    ...(showMultiPageHint
      ? [
          {
            key: "multi-page-report",
            severity: "info",
            text: translate(
              "This looks like part of a multi-page report. Please add the remaining pages.",
              "يبدو أن هذا جزء من تقرير متعدد الصفحات. يرجى إضافة الصفحات المتبقية."
            ),
          },
        ]
      : []),
    ...(duplicateSideSuspected
      ? [
          {
            key: "duplicate-side",
            severity: "warning",
            text:
              duplicatedSide === "front"
                ? translate(
                    "Both files look like the front of the document — we still need the back.",
                    "يبدو أن كلا الملفين هما الوجه الأمامي من المستند — ما زلنا بحاجة إلى الوجه الخلفي."
                  )
                : duplicatedSide === "back"
                ? translate(
                    "Both files look like the back of the document — we still need the front.",
                    "يبدو أن كلا الملفين هما الوجه الخلفي من المستند — ما زلنا بحاجة إلى الوجه الأمامي."
                  )
                : translate(
                    "The two uploaded files look like the same side of the document. Please make sure one is the front and one is the back.",
                    "يبدو أن الملفين المحمّلين هما نفس الوجه من المستند. يرجى التأكد من تحميل الوجه الأمامي والوجه الخلفي."
                  ),
          },
        ]
      : []),
    ...(needsOtherSide
      ? [
          {
            key: "needs-other-side",
            severity: "info",
            text:
              uploadedSide === "front"
                ? translate(
                    "You've uploaded the front — please also upload the back side of the document.",
                    "لقد قمت بتحميل الوجه الأمامي — يرجى أيضًا تحميل الوجه الخلفي من المستند."
                  )
                : uploadedSide === "back"
                ? translate(
                    "You've uploaded the back — please also upload the front side of the document.",
                    "لقد قمت بتحميل الوجه الخلفي — يرجى أيضًا تحميل الوجه الأمامي من المستند."
                  )
                : translate(
                    `${files.length} of ${minFiles} uploaded — please also upload the other side of the document (front and back are both required).`,
                    `تم تحميل ${files.length} من ${minFiles} — يرجى أيضًا تحميل الوجه الآخر من المستند (الوجهان الأمامي والخلفي مطلوبان).`
                  ),
          },
        ]
      : []),
    // Display only: neutral, never blocking. Claims reviewed this copy and
    // asked for a bare statement — no issuing country, and no mention of the
    // relaxed cross-document checks or the waived front/back requirement,
    // both of which still apply.
    ...(hasForeignFile
      ? [
          {
            key: "foreign",
            severity: "info",
            text: translate(
              "Non-Bahraini document/s identified",
              "تم تحديد مستند/مستندات غير بحرينية"
            ),
          },
        ]
      : []),
  ];

  const continueClickHandler = async () => {
    if (continueDisabled) return;
    const invalidLinks = files.filter(
      (file) =>
        !file.preview ||
        !file.preview.startsWith("https://files-api.solidarity.bh")
    );
    if (invalidLinks.length > 0) {
      toast.error(
        translate(
          "Some files were not uploaded correctly. Please remove and re-upload them.",
          "لم يتم تحميل بعض الملفات بشكل صحيح. يرجى إزالتها وإعادة تحميلها."
        )
      );
      return;
    }
    if (isETrafficReport) await saveETrafficAck({ acknowledged: true });
    await saveMotorDataInRedux(files);
    handleNextSubstep();
  };

  // Keep this page's usable uploads on Back as well: a page held by a
  // blocking cross-check never reaches Continue, and the customer must be
  // able to go back, fix the other document and return without re-uploading
  // this one. Files still uploading/analyzing are not kept (their result
  // dies with the page's timers, and an unvalidated file must not come back
  // as merely "uploaded"); failed ones must be removed anyway, and redux
  // freezes stored files so a restored failure could not be retried.
  const backClickHandler = async () => {
    await saveMotorDataInRedux(
      files.filter((file) => ["verified", "uploaded"].includes(stateOf(file)))
    );
    handlePrevSubstep();
  };

  // The swap from "being read" to a verdict is the moment this page pays off,
  // and it used to be a hard cut between two differently coloured cards.
  // popLayout takes the outgoing scan card out of flow so the settled card
  // lands in its place instead of the row collapsing and reopening, and the
  // blur bridges the two so the eye reads one card changing rather than two
  // cards briefly overlapping.
  const settledCardMotion = {
    initial: {
      opacity: 0,
      y: reduceMotion ? 0 : 6,
      filter: reduceMotion ? "blur(0px)" : "blur(4px)",
    },
    animate: { opacity: 1, y: 0, filter: "blur(0px)" },
    transition: { duration: 0.26, ease: EASE_OUT },
  };
  const scanCardExit = {
    opacity: 0,
    scale: reduceMotion ? 1 : 0.985,
    filter: reduceMotion ? "blur(0px)" : "blur(4px)",
    // Exit runs shorter than the entrance: the verdict is what the customer
    // is waiting for, so nothing should sit in front of it.
    transition: { duration: 0.18, ease: EASE_OUT },
  };

  const renderFileCard = (file) => {
    const state = stateOf(file);
    const scanning = state === "uploading" || state === "analyzing";
    // Watching a file through its scan is what earns the result card its tick
    // animation. Files restored from redux on Back never went through one on
    // this mount, so they settle silently instead of the page announcing
    // itself with two ticks on arrival.
    if (scanning) scannedKeysRef.current.add(file._key);
    return (
      // initial={false} so files restored from redux on Back simply exist —
      // a page that animates itself in on every visit is choreography, not
      // feedback. The scan card carries its own one-shot CSS entrance, which
      // only ever plays when a file has just been dropped.
      <AnimatePresence key={file._key} mode="popLayout" initial={false}>
        {scanning ? (
          <motion.div key="scanning" exit={scanCardExit}>
            <DocumentScanCard
              file={file}
              mode={state}
              onCancel={() => handleRemove(file)}
            />
          </motion.div>
        ) : (
          <motion.div key="settled" {...settledCardMotion}>
            <DocumentResultCard
              file={file}
              state={state}
              justRead={scannedKeysRef.current.has(file._key)}
              onRemove={() => handleRemove(file)}
              onRetry={() => handleRetryUpload(file)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <FileClaimMotorLayout
      btn_disabled={continueDisabled}
      // Back is held for the same reason Continue is: a file still uploading
      // or being read has no verdict yet, and leaving the page throws away the
      // read that was going to produce one — backClickHandler only keeps the
      // files that settled.
      back_disabled={isBusy}
      btn_onClick={continueClickHandler}
      back_onClick={backClickHandler}
    >
      {/* `relative` for the cards' exit animation: AnimatePresence popLayout
          takes the outgoing card out of flow by measuring it against its
          offsetParent and re-pinning it with position:absolute — which
          resolves against the nearest POSITIONED ancestor. With nothing
          positioned between the two, those are different boxes and the card
          spends its exit somewhere else on the page. */}
      <div className="relative mx-auto bg-white max767:p-5 w-[500px] max-w-[100%] pb-20">
        <TitleDescription
          title={translate(
            "Upload Required Documents",
            "تحميل المستندات المطلوبة"
          )}
          description={translate(config.subtitle_en, config.subtitle_ar)}
        />

        {/* What the slot takes, said before the customer finds out by being
            refused — every file they hand over costs a paid OCR call, and the
            page they photograph first is rarely the last one.
            Rendered through the same component as Vista's own findings so it
            carries their shell, icon and spacing: this is the page telling the
            customer something about their documents, and there is no reason
            for it to be a different kind of object from the rest.
            Only where the number is genuinely the customer's to choose: a
            single-file slot says nothing and simply behaves like one, and a
            front/back document is already described by its own subtitle. */}
        {slotMax > (config.minFiles || 1) && (
          <ValidationNotices
            className="mt-0 mb-4"
            notices={[
              {
                key: "slot-allowance",
                severity: "info",
                text:
                  files.length === 0
                    ? translate(
                        `You can add up to ${maxFiles} ${unitLabel}.`,
                        `يمكنك إضافة ما يصل إلى ${maxFiles} ${unitLabel}.`
                      )
                    : translate(
                        `${files.length} of ${maxFiles} ${unitLabel} added.`,
                        `تمت إضافة ${files.length} من ${maxFiles} ${unitLabel}.`
                      ),
              },
            ]}
          />
        )}

        {files.map((file) => renderFileCard(file))}

        {files.length < maxFiles && (
          // The empty slot is the same object as the cards above it, in the
          // state before it holds anything: same Card, same radius, same
          // width. Dashed rather than solid is the whole difference, and it is
          // now visible at rest — the border used to be `transparent` until a
          // file was dragged over, which left a flat grey rectangle with no
          // affordance on it and put the one piece of feedback behind the
          // gesture it was meant to invite.
          //
          // Inside it, three things at three weights: what to do, the button
          // that does it, and what the slot will take. They used to be one
          // size in one colour, which is why nothing in here was findable.
          <Card
            {...getRootProps({
              className: cn(
                "dropzone group flex flex-col items-center justify-center gap-2.5",
                "min-h-[148px] px-5 py-5 text-center cursor-pointer",
                "rounded-[10px] border-2 border-dashed shadow-none",
                "transition-[background-color,border-color] duration-200 ease-out",
                "motion-reduce:transition-none",
                // focus-visible, not focus: the root takes keyboard focus so
                // Enter opens the picker, but a mouse user who has just
                // clicked it should not be left with a ring.
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                isDragActive
                  ? "border-primary bg-[#F0FAFF]"
                  : "border-border bg-muted hover:border-primary/45 hover:bg-[#F4FAFF]"
              ),
            })}
          >
            <input {...getInputProps()} />
            {/* Icon beside the sentence, not stacked above it. Three centred
                rows is the shape every dropzone has; two is calmer, shorter,
                and makes the mark and the instruction read as one thing. */}
            <div className="flex items-center gap-2.5">
              <span
                aria-hidden="true"
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]",
                  "transition-colors duration-200 ease-out",
                  "motion-reduce:transition-none",
                  isDragActive
                    ? "bg-primary/20"
                    : "bg-primary/10 group-hover:bg-primary/15"
                )}
              >
                {/* lucide, not the Upload.svg asset: that file is filled
                    #DDE2E4 — the border grey — so it was a light grey glyph on
                    a near-white box, invisible at arm's length. The rest of
                    this flow's iconography is lucide already (the cards, the
                    notices), and an icon that can take a colour is the
                    point. */}
                <Upload
                  className={cn(
                    "h-[18px] w-[18px] transition-colors duration-200 ease-out",
                    "motion-reduce:transition-none",
                    isDragActive ? "text-[#055B80]" : "text-[#076A96]"
                  )}
                />
              </span>
              <p className="text-[#252C32] text-base not-italic font-semibold leading-6 text-start">
                {isDragActive
                  ? translate("Drop to upload", "أفلت الملفات للتحميل")
                  : translate("Drag and drop files here", "اسحب الملفات هنا")}
              </p>
            </div>
            {/* The one control in the box, and it has to look like one. At
                the small size it read as a third pale pill stacked under two
                others — the portal's own button rhythm (h-9, 14px, semibold)
                is what makes it a thing you press rather than another label. */}
            <Button
              type="button"
              variant="outline"
              // One tab stop for one action: the drop area itself is already
              // focusable and opens the picker on Enter, so the button inside
              // it is a mouse affordance, not a second destination.
              tabIndex={-1}
              className={cn(
                "h-9 px-4 text-sm font-semibold tracking-[-0.084px]",
                "border-[#CFD8DC] bg-white text-[#48535B]",
                "shadow-[0_1px_2px_rgba(11,47,66,0.06)]",
                "hover:bg-white hover:border-[#9FDCF5] hover:text-[#076A96]",
                "motion-reduce:transition-none"
              )}
            >
              {translate("Browse", "تصفح")}
            </Button>
            <AcceptedFilesNote
              types={acceptedTypesForSlot}
              maxSizeMb={MAX_FILE_SIZE_BYTES / (1024 * 1024)}
            />
          </Card>
        )}

        <ValidationNotices notices={validationNotices} />

        {isETrafficReport && (
          <CustomCheckbox
            className="mt-4"
            isChecked={eTrafficAcknowledged}
            setIsChecked={setETrafficAcknowledged}
            Label={
              <span className="text-[#252C32] text-sm not-italic font-normal leading-6">
                {translate(
                  "Kindly confirm that the at fault party in the accident has acknowledged his/her fault through e-traffic application",
                  "يرجى تأكيد أن الطرف المتسبب في الحادث قد أقرّ بمسؤوليته عبر تطبيق المرور الإلكتروني (e-traffic)"
                )}
              </span>
            }
          />
        )}

        {config.example && (
          <div className="bg-[#F6F8F9] rounded-[10px] p-4 mt-6">
            <p className="text-[#252C32] text-center text-xs font-semibold uppercase tracking-wide mb-3">
              {translate(config.example.title_en, config.example.title_ar)}
            </p>
            {config.example.note_en && (
              <p className="text-[#252C32] text-center text-sm mb-3">
                {translate(config.example.note_en, config.example.note_ar)}
              </p>
            )}
            <div
              className={
                config.example.singleImage
                  ? ""
                  : "flex gap-2 max767:flex-col max767:items-center"
              }
            >
              {config.example.images.map((src, idx) => (
                <img
                  key={idx}
                  src={src}
                  alt={translate("Example document", "مستند نموذجي")}
                  className={
                    config.example.singleImage
                      ? "w-full rounded-md"
                      : "w-1/2 max767:w-full rounded-md object-contain self-start"
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </FileClaimMotorLayout>
  );
}

export default UploadDocStepPage;
